import { useState, useEffect, useCallback } from 'react';
import { Bell, User, MessageSquare, Heart, Award, Check, Star, Clock, ThumbsUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { getNotifications, markNotificationsAsRead, subscribeToNotifications } from '@/lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { useConnectionContext } from '@/contexts/ConnectionContext';
import { checkForLevelUpNotifications } from '@/lib/levelManager';
import { checkForBadgeNotifications } from '@/lib/badgeManager';

// Types for our notifications
interface NotificationProps {
  id: string;
  type: string;
  content: string;
  created_at: string;
  is_read: boolean;
  actor?: {
    id: string;
    display_name: string;
    username: string;
    avatar_url: string;
  };
  entity_id?: string;
  entity_type?: string;
}

const NotificationItem = ({ notification, onMarkAsRead }: { notification: NotificationProps, onMarkAsRead: (id: string) => void }) => {
  const navigate = useNavigate();
  
  const getIcon = () => {
    switch (notification.type) {
      case 'mention':
        return <Bell className="h-5 w-5 text-purple-500" />;
      case 'like':
        return <Heart className="h-5 w-5 text-pink-500" />;
      case 'comment':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'follow':
        return <User className="h-5 w-5 text-green-500" />;
      case 'upvote':
        return <ThumbsUp className="h-5 w-5 text-indigo-500" />;
      case 'achievement':
        return <Award className="h-5 w-5 text-amber-500" />;
      case 'message':
        return <MessageSquare className="h-5 w-5 text-cyan-500" />;
      case 'system':
        return <Star className="h-5 w-5 text-vibebops-accent" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      return 'some time ago';
    }
  };
  
  const handleNotificationClick = () => {
    if (notification.type === 'message') {
      // For message notifications, go to messages page
      navigate('/messages');
      return;
    }
    
    if (notification.entity_id && notification.entity_type) {
      if (notification.entity_type === 'post') {
        // Navigate to the post
        navigate(`/home?postId=${notification.entity_id}`);
      } else if (notification.entity_type === 'profile' && notification.actor) {
        // Navigate to the user's profile
        navigate(`/profile/${notification.entity_id}`);
      } else if (notification.entity_type === 'badge') {
        // Navigate to the user's badge collection
        navigate(`/profile?tab=badges`);
      } else if (notification.entity_type === 'level') {
        // Navigate to the user's profile (general)
        navigate(`/profile`);
      }
    }
  };
  
  const handleMouseEnter = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
  };
  
  const isClickable = (notification.entity_id && 
    (notification.entity_type === 'post' || 
     notification.entity_type === 'profile' || 
     notification.entity_type === 'badge' || 
     notification.entity_type === 'level')) ||
    notification.type === 'message';

  return (
    <Card 
      className={`mb-4 ${!notification.is_read ? 'border-l-4 border-l-vibebops-accent' : ''} 
        ${isClickable ? 'cursor-pointer hover:bg-vibebops-highlight/50 transition-colors' : ''}`}
      onClick={isClickable ? handleNotificationClick : undefined}
      onMouseEnter={handleMouseEnter}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <div className="rounded-full p-2 bg-vibebops-highlight">
          {getIcon()}
        </div>
        
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              {notification.actor && (
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-6 w-6 rounded-full overflow-hidden">
                    <img 
                      src={notification.actor.avatar_url || '/placeholder.svg'} 
                      alt={notification.actor.display_name} 
                      className="h-full w-full object-cover" 
                    />
                  </div>
                  <span className="font-bold">{notification.actor.display_name}</span>
                  <span className="text-vibebops-textMuted text-sm">@{notification.actor.username}</span>
                </div>
              )}
              <p className={`text-sm ${!notification.is_read ? 'font-semibold' : ''}`}>
                {notification.content}
              </p>
            </div>
            
            <div className="flex flex-col items-end gap-1">
              <span className="text-xs text-vibebops-textMuted">{formatTimestamp(notification.created_at)}</span>
              {!notification.is_read && <div className="w-2 h-2 rounded-full bg-vibebops-accent"></div>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Notifications = () => {
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();
  const { isConnected } = useConnectionContext();
  
  const unreadCount = notifications.filter(n => !n.is_read).length;
  
  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications.filter(n => !n.is_read);
  
  // Function to fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const fetchedNotifications = await getNotifications(user.id);
      setNotifications(fetchedNotifications);
      
      // Check for level-up notifications and show toast if needed
      checkForLevelUpNotifications(fetchedNotifications);
      
      // Check for badge notifications and show toast if needed
      checkForBadgeNotifications(fetchedNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast({
        title: "Error",
        description: "Failed to load notifications. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);
  
  // Mark a single notification as read
  const markAsRead = async (notificationId: string) => {
    if (!user) return;
    
    try {
      const success = await markNotificationsAsRead(user.id, [notificationId]);
      
      if (success) {
        // Update local state to mark this notification as read
        setNotifications(prevNotifications => 
          prevNotifications.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true } 
              : notification
          )
        );
      }
    } catch (error) {
      console.error(`Error marking notification ${notificationId} as read:`, error);
    }
  };
  
  // Fetch notifications and set up real-time subscription
  useEffect(() => {
    fetchNotifications();
    
    // Set up real-time notifications subscription
    let unsubscribe: (() => void) | null = null;
    
    if (user) {
      unsubscribe = subscribeToNotifications(user.id, (notification) => {
        console.log('Notification page received real-time notification:', notification);
        // Add the new notification to the list at the top
        setNotifications(prev => [notification as NotificationProps, ...prev]);
      });
      
      // Listen for sign out event
      const handlePreSignout = () => {
        console.log('Notifications component: Cleaning up subscriptions before sign out');
        if (unsubscribe) {
          unsubscribe();
        }
      };
      
      window.addEventListener('app:pre-signout', handlePreSignout);
      
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
        window.removeEventListener('app:pre-signout', handlePreSignout);
      };
    }
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, fetchNotifications]);
  
  // Refresh notifications when connection state changes (after reconnection)
  useEffect(() => {
    if (isConnected && user) {
      console.log('Connection restored or changed, refreshing notifications');
      fetchNotifications();
    }
  }, [isConnected, user, fetchNotifications]);

  const markAllAsRead = async () => {
    if (!user) return;
    
    if (unreadCount === 0) {
      toast({
        title: "No unread notifications",
        description: "All your notifications are already read.",
      });
      return;
    }
    
    try {
      const success = await markNotificationsAsRead(user.id);
      
      if (success) {
        // Update local state to mark all as read
        const updatedNotifications = notifications.map(notification => ({
          ...notification,
          is_read: true
        }));
        
        setNotifications(updatedNotifications);
        
        toast({
          title: "Notifications marked as read",
          description: `${unreadCount} notification${unreadCount > 1 ? 's' : ''} marked as read.`,
        });
      } else {
        throw new Error("Failed to mark notifications as read");
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark notifications as read. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold mb-2">Notifications</h1>
        <p className="text-vibebops-textMuted">Stay updated with what's happening in your network.</p>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <Tabs defaultValue="all" className="w-[200px]" onValueChange={(value) => setFilter(value as 'all' | 'unread')}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="unread">
              Unread
              {unreadCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-vibebops-accent text-white">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-1"
            onClick={markAllAsRead}
            disabled={isLoading || unreadCount === 0}
          >
            <Check className="h-4 w-4" />
            Mark all as read
          </Button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-vibebops-accent rounded-full border-t-transparent"></div>
        </div>
      ) : (
        <div>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map(notification => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
                onMarkAsRead={markAsRead}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-vibebops-highlight rounded-full p-6 mb-4">
                <Bell className="h-12 w-12 text-vibebops-textMuted" />
              </div>
              <h3 className="text-xl font-bold mb-2">No {filter === 'unread' ? 'unread ' : ''}notifications</h3>
              <p className="text-vibebops-textMuted text-center max-w-xs">
                {filter === 'unread' 
                  ? "You've caught up with all your notifications! Check back later for updates."
                  : "You don't have any notifications yet. When you get notifications, they'll show up here."}
              </p>
            </div>
          )}
        </div>
      )}
      
      <div className="flex justify-center items-center mt-8">
        <div className="flex gap-2 items-center text-sm text-vibebops-textMuted">
          <Clock className="h-4 w-4" />
          <span>Notifications are updated in real-time</span>
        </div>
      </div>
    </MainLayout>
  );
};

export default Notifications;
